import { sanitizeError, sanitizeObject } from '../security/sensitiveDataSanitizer';
import type {
  BreadcrumbEntry,
  ErrorCategory,
  ErrorImpact,
  ErrorSeverity,
  ErrorSource,
  NormalizedErrorEvent,
} from '../types/error.types';
import type { ObservabilityContext } from '../types/observability.types';
import { buildStableErrorHash } from './hash';
import { nowIso } from './time';

function generateId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

type NormalizeErrorInput = {
  error: unknown;
  source: ErrorSource;
  category?: ErrorCategory;
  severity?: ErrorSeverity;
  impact?: ErrorImpact;
  context: ObservabilityContext;
  breadcrumbs?: BreadcrumbEntry[];
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};

function categorizeFromError(error: unknown, source: ErrorSource): ErrorCategory {
  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  if (source === 'database') return 'database';
  if (source === 'iot') return 'iot';
  if (source === 'job') return 'job';
  if (message.includes('network') || message.includes('fetch')) return 'network';
  if (message.includes('permission') || message.includes('forbidden')) return 'permission';
  if (message.includes('auth') || message.includes('unauthorized') || message.includes('token'))
    return 'auth';
  if (message.includes('validation')) return 'validation';
  if (message.includes('timeout') || message.includes('slow')) return 'performance';
  return 'runtime';
}

function defaultSeverity(category: ErrorCategory): ErrorSeverity {
  switch (category) {
    case 'network':
      return 'warning';
    case 'validation':
      return 'warning';
    case 'permission':
    case 'auth':
      return 'error';
    case 'database':
      return 'critical';
    default:
      return 'error';
  }
}

function defaultImpact(source: ErrorSource): ErrorImpact {
  switch (source) {
    case 'database':
      return 'system_wide';
    case 'iot':
      return 'device_control_risk';
    case 'backend':
      return 'multi_user';
    default:
      return 'single_user';
  }
}

export function normalizeErrorEvent(input: NormalizeErrorInput): NormalizedErrorEvent {
  const sanitized = sanitizeError(input.error);
  const category = input.category ?? categorizeFromError(input.error, input.source);
  const severity = input.severity ?? defaultSeverity(category);
  const impact = input.impact ?? defaultImpact(input.source);
  const timestamp = nowIso();
  const hash = buildStableErrorHash({
    message: sanitized.message,
    stack: sanitized.stack,
    source: input.source,
    category,
    route: input.context.route,
    service: input.context.service,
    releaseId: input.context.releaseId,
  });

  return {
    id: generateId(),
    hash,
    message: sanitized.message,
    name: sanitized.name,
    stack: sanitized.stack,
    source: input.source,
    category,
    severity,
    impact,
    context: {
      ...input.context,
      timestamp,
    },
    breadcrumbs: input.breadcrumbs ? sanitizeObject(input.breadcrumbs) : undefined,
    tags: input.tags,
    extra: input.extra ? sanitizeObject(input.extra) : undefined,
    firstSeenAt: timestamp,
    lastSeenAt: timestamp,
    occurrenceCount: 1,
  };
}
